import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Search, Mail, Phone, X } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminSelect, AdminTextarea, StatusPill, EmptyState } from "@/admin/components/ui";

const STATUSES = ["all", "new", "read", "replied", "archived"];
const SET_STATUSES = ["new", "read", "replied", "archived"];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [stat, setStat] = useState("new");

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/contacts").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => items.filter((m) =>
    (status === "all" || (m.status || "new") === status) &&
    (!q || `${m.name} ${m.email} ${m.message}`.toLowerCase().includes(q.toLowerCase()))
  ), [items, q, status]);

  const openDetail = (m) => {
    setOpen(m);
    setAdminNotes(m.admin_notes || "");
    setStat(m.status || "new");
  };

  const update = async () => {
    try {
      await adminApi.put(`/admin/contacts/${open.id}`, { status: stat, admin_notes: adminNotes });
      toast.success("Message updated.");
      setOpen(null); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="page-admin-contacts" className="space-y-6">
      <SEO title="AdminContacts · WelkomBiz Photography" noindex />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`contacts-filter-${s}`} onClick={() => setStatus(s)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${status === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{s}</button>
        ))}
      </div>

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       list.length === 0 ? <EmptyState testId="contacts-empty" title="No messages match" message="Try a different filter or search." /> :
       (
        <Panel className="!p-0 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-left px-5 py-3">Preview</th>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map((m) => (
                  <tr key={m.id} data-testid={`contact-row-${m.id}`} onClick={() => openDetail(m)} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                    <td className="px-5 py-3 text-white">{m.name}</td>
                    <td className="px-5 py-3 text-white/70">{m.email}</td>
                    <td className="px-5 py-3 text-white/60">{m.phone || "—"}</td>
                    <td className="px-5 py-3 text-white/50 max-w-[320px] truncate">{m.message}</td>
                    <td className="px-5 py-3 text-white/45 text-xs">{fmtDate(m.created_at)}</td>
                    <td className="px-5 py-3"><StatusPill status={m.status || "new"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {list.map((m) => (
              <button key={m.id} data-testid={`contact-card-${m.id}`} onClick={() => openDetail(m)} className="w-full text-left p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white truncate">{m.name}</p>
                    <p className="text-xs text-white/55 truncate">{m.email}</p>
                  </div>
                  <StatusPill status={m.status || "new"} />
                </div>
                <p className="text-xs text-white/55 mt-2 line-clamp-2">{m.message}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-2">{fmtDate(m.created_at)}</p>
              </button>
            ))}
          </div>
        </Panel>
       )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="contact-detail-modal" className="glass-strong chrome-border rounded-md p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div className="min-w-0">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Contact Message</p>
                <h3 className="font-display text-2xl text-chrome">{open.name}</h3>
                <p className="text-xs text-white/50 mt-1">{fmtDate(open.created_at)}</p>
              </div>
              <button onClick={() => setOpen(null)} className="w-9 h-9 rounded-full chrome-border bg-black/40 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div className="glass rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email</p><a className="text-sm text-white" href={`mailto:${open.email}`}>{open.email}</a></div>
              <div className="glass rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</p><p className="text-sm text-white">{open.phone || "—"}</p></div>
            </div>
            <div className="glass rounded-md p-4 mb-5">
              <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">Message</p>
              <p className="text-sm text-white/85 font-body whitespace-pre-wrap">{open.message}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdminSelect data-testid="contact-status-select" label="Status" value={stat} onChange={(e) => setStat(e.target.value)}>
                {SET_STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
              </AdminSelect>
              <div />
              <AdminTextarea data-testid="contact-notes" label="Admin Notes" rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="sm:col-span-2" />
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="contact-update" onClick={update} className="btn-primary !py-2.5 !px-5 !text-[11px]">Update Message</button>
              <button onClick={() => setOpen(null)} className="btn-ghost !py-2.5 !px-5 !text-[11px]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
